import { useContext, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import t from 'react-translate';
import { config } from '@config/pendo.config.json';
import moment from 'moment';
import { Placement } from 'react-bootstrap/esm/Overlay';

import MentoringProgramContext from 'athena/components/mentoring-program/context';

// Styles
import { css } from '@emotion/react';
import { gray6, info } from 'styles/global_defaults/colors';

// Components
import { Button } from 'react-bootstrap';
import NvPopover from 'shared/components/nv-popover';
import AvailabilityCalendar from './availability-calendar';

const styles = css`
  .bs4-btn-link {
    &:focus, &:active, &:hover {
      text-decoration: none;
      box-shadow: none !important;
    }
  }
`;

const overlayStyles = css`
  min-width: 455px;
  z-index: 5091;
  border: 1px solid ${gray6};
  .bs4-popover-body {
    padding: 0;
  }
  .arrow {
    transform: translate3d(90px, 0px, 0px) !important;
    &::before {
      border-bottom-color: ${gray6} !important;
    }
    &::after {
      border-bottom-color: ${info} !important;
    }
  }
`;

const getEndDate = (endsOn: string, programEndDate?: string) => {
  if (programEndDate && endsOn) {
    return moment.min(moment(endsOn), moment(programEndDate)).toISOString();
  }
  return moment(endsOn).toISOString()
    || moment(programEndDate).toISOString()
    || '';
};

type SchedulingWithAvailabilityProps = {
  placement: Placement;
};

const SchedulingWithAvailability = (props: SchedulingWithAvailabilityProps) => {
  const { placement = 'bottom-start' } = props;
  const translations = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.EDIT_MODE.SCHEDULING_WITH_AVAILABILITY;
  const [showAvailabilityCalendar, setShowAvailabilityCalendar] = useState(false);
  const dataQATag = config.pendo.athena.mentorshipProgram.participantHome.sessions.planSessionFlyout.schedulingWithAvailability;
  const { mentoringProgram } = useContext(MentoringProgramContext);

  const { watch, setValue } = useFormContext();
  const scheduledDate = watch('scheduledDate');
  const endsOn = watch('endsOn');
  const selectedDate = scheduledDate ? moment(scheduledDate).toISOString() : '';
  const endDate = getEndDate(endsOn, mentoringProgram?.endDate);
  const setSelectedDate = (date: string) => {
    setValue('scheduledDate', date, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setShowAvailabilityCalendar(false);
  };

  return (
    <div
      css={styles}
      id='view-suggested-meeting-times'
      className='scheduling-with-availability mb-4'
    >
      <NvPopover
        enabled
        rootClose
        placement={placement}
        overlayStyles={overlayStyles}
        show={showAvailabilityCalendar}
        content={(
          <AvailabilityCalendar
            selectedDate={selectedDate}
            endDate={endDate}
            setSelectedDate={setSelectedDate}
          />
        )}
        onHide={() => setShowAvailabilityCalendar(false)}
      >
        <Button
          variant='link'
          className='text-regular text-primary p-0 semi-bold'
          data-qa={dataQATag.openCalendar}
          aria-label={translations.ARIA_LABEL()}
          onClick={() => setShowAvailabilityCalendar(!showAvailabilityCalendar)}
        >
          {translations.BUTTON()}
        </Button>
      </NvPopover>
    </div>
  );
};

export default SchedulingWithAvailability;
